import { Alert } from '@mantine/core';
import { IconCircleCheck } from '@tabler/icons-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../format.js';
import { SortableTh, type SortState, TablePager } from '../purchasing/table.js';
import classes from './Condition.module.css';
import type { CmPointValuesView, CmReport, CmRowIssue } from './condition-types.js';
import { limitLabel, measureNumber } from './condition-view.js';
import pms from './Pms.module.css';
import work from './PmsWork.module.css';
import { paginate } from './plan-view.js';

export interface PreviewTile {
  key: string;
  label: string;
  value: ReactNode;
  tone?: 'danger';
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <section>
      <h3 className={classes.sectionTitle}>{title}</h3>
      <section
        className={pms.tableBox}
        aria-label={t('pms.equipment.tableOf', { name: title })}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
        tabIndex={0}
      >
        {children}
      </section>
    </section>
  );
}

type ErrorSort = 'row' | 'problem';

/**
 * İçe aktarma önizlemesi (plan M7 Task 21a; Task 25'te kalem listesinde yeniden kullanılır): özet
 * kutuları, satır numaralı hata tablosu (sıralanır, sayfalanır), uyarılar ve içe aktarmaya özgü
 * bölümler. Önizleme hiçbir şey yazmaz; hatalı dosya uygulanmaz.
 */
export function ImportPreview({
  tiles,
  errors,
  warnings,
  codeText,
  children,
  pageSize = 10,
}: {
  tiles: readonly PreviewTile[];
  errors: readonly CmRowIssue[];
  warnings: readonly { row: number; code: string }[];
  codeText: (code: string) => string;
  children?: ReactNode;
  pageSize?: number;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pms.condition.cm.${key}`);
  const [sort, setSort] = useState<SortState<ErrorSort>>({ key: 'row', dir: 'asc' });
  const [page, setPage] = useState(1);
  const sorted = useMemo(() => {
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...errors].sort((a, b) =>
      sort.key === 'row'
        ? sign * (a.row - b.row)
        : sign * codeText(a.code).localeCompare(codeText(b.code)) || a.row - b.row,
    );
  }, [errors, sort, codeText]);
  const view = paginate(sorted, page, pageSize);
  return (
    <div className={classes.stack}>
      <div className={classes.summary}>
        {tiles.map((tile) => (
          <div key={tile.key} className={classes.summaryTile} data-tone={tile.tone}>
            <span>{tile.label}</span>
            <strong>{tile.value}</strong>
          </div>
        ))}
      </div>
      {errors.length === 0 ? (
        <Alert color="green" icon={<IconCircleCheck size={16} />} role="status">
          {k('noErrors')}
        </Alert>
      ) : (
        <Section title={k('errorsTitle')}>
          <table className={pms.table} data-compact data-cards>
            <thead>
              <tr>
                <SortableTh
                  column="row"
                  label={k('row')}
                  sort={sort}
                  numeric
                  onSort={(next) => {
                    setSort(next);
                    setPage(1);
                  }}
                />
                <th scope="col">{k('column')}</th>
                <SortableTh
                  column="problem"
                  label={k('problem')}
                  sort={sort}
                  onSort={(next) => {
                    setSort(next);
                    setPage(1);
                  }}
                />
                <th scope="col">{k('value')}</th>
              </tr>
            </thead>
            <tbody>
              {view.rows.map((issue) => (
                <tr key={`${issue.row}-${issue.column ?? ''}-${issue.code}-${issue.value ?? ''}`}>
                  <td data-label={k('row')} data-numeric>
                    {issue.row}
                  </td>
                  <td data-label={k('column')}>{issue.column ?? '—'}</td>
                  <td data-label={k('problem')} className={classes.parameter}>
                    {codeText(issue.code)}
                  </td>
                  <td data-label={k('value')}>{issue.value ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}
      {errors.length > pageSize ? (
        <TablePager
          page={view.page}
          pages={view.pages}
          summary={t('pms.work.showing', { from: view.from, to: view.to, total: view.total })}
          label={t('pms.work.pages', { name: k('errorsTitle') })}
          onPage={setPage}
        />
      ) : null}
      {warnings.length > 0 ? (
        <section>
          <h3 className={classes.sectionTitle}>{k('warningsTitle')}</h3>
          <ul className={work.detailList}>
            {warnings.map((warning) => (
              <li key={`${warning.row}-${warning.code}`}>
                <span className={work.detailListMain}>
                  <strong>{`${k('row')} ${warning.row}`}</strong>
                  <span className={work.note}>{codeText(warning.code)}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {children}
    </div>
  );
}

const CHANGE_FIELDS = [
  'unit',
  'alertLow',
  'alertHigh',
  'dangerLow',
  'dangerHigh',
  'limitSource',
  'provider',
  'baselineValue',
  'lowInclusive',
  'highInclusive',
] as const;

/** CM önizlemesi (K19, K20): yeni noktalar, eşik ve baseline değişiklikleri, açılacak arızalar. */
export function CmImportPreview({
  report,
  pointNames,
}: {
  report: CmReport;
  /** Var olan noktaların adı (değişiklik ve arıza satırları). */
  pointNames: ReadonlyMap<string, string>;
}) {
  const { t, i18n } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`pms.condition.cm.${key}`, options);
  const language = i18n.resolvedLanguage ?? 'en';
  const codeText = (code: string) => t(`pms.errors.${code}`, { defaultValue: code });
  const show = (field: string, values: CmPointValuesView) => {
    const value = (values as unknown as Record<string, unknown>)[field];
    if (value === null || value === undefined) return '—';
    if (typeof value === 'number') return measureNumber(language, value);
    if (typeof value === 'boolean') return value ? '✓' : '—';
    if (field === 'limitSource') {
      return t(`pms.condition.limitSources.${String(value)}`, { defaultValue: String(value) });
    }
    return String(value);
  };
  const nameOf = (ref: CmReport['defectsToOpen'][number]['point']) =>
    'pointId' in ref
      ? (pointNames.get(ref.pointId) ?? '—')
      : (report.pointsToCreate[ref.newPointIndex]?.name ?? '—');
  return (
    <ImportPreview
      tiles={[
        { key: 'rows', label: k('summary.rows'), value: report.rowsTotal },
        { key: 'created', label: k('summary.created'), value: report.pointsToCreate.length },
        { key: 'updated', label: k('summary.updated'), value: report.pointsToUpdate.length },
        {
          key: 'measurements',
          label: k('summary.measurements'),
          value: report.measurements.total,
        },
        {
          key: 'defects',
          label: k('summary.defects'),
          value: report.defectsToOpen.length,
          ...(report.defectsToOpen.length > 0 ? { tone: 'danger' as const } : {}),
        },
      ]}
      errors={report.errors}
      warnings={report.warnings}
      codeText={codeText}
    >
      {report.measurements.total > 0 ? (
        <p className={work.note}>
          {k('readingsSplit', {
            normal: report.measurements.normal,
            abnormal: report.measurements.abnormal,
            fail: report.measurements.fail,
          })}
        </p>
      ) : null}
      {report.pointsToCreate.length > 0 ? (
        <Section title={k('createdTitle')}>
          <table className={pms.table} data-compact data-cards>
            <thead>
              <tr>
                <th scope="col">{k('row')}</th>
                <th scope="col">{k('equipment')}</th>
                <th scope="col">{k('point')}</th>
                <th scope="col">{k('limit')}</th>
              </tr>
            </thead>
            <tbody>
              {report.pointsToCreate.map((p) => (
                <tr key={`${p.row}-${p.name}`}>
                  <td data-label={k('row')}>{p.row}</td>
                  <td data-label={k('equipment')}>{p.equipmentCode}</td>
                  <td data-label={k('point')}>{p.name}</td>
                  <td data-label={k('limit')}>{limitLabel(p, p.unit, language)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      ) : null}
      {report.pointsToUpdate.length > 0 ? (
        <Section title={k('changesTitle')}>
          <table className={pms.table} data-compact data-cards>
            <thead>
              <tr>
                <th scope="col">{k('row')}</th>
                <th scope="col">{k('point')}</th>
                <th scope="col">{k('change')}</th>
              </tr>
            </thead>
            <tbody>
              {report.pointsToUpdate.map((u) => (
                <tr key={`${u.row}-${u.pointId}`}>
                  <td data-label={k('row')}>{u.row}</td>
                  <td data-label={k('point')}>{pointNames.get(u.pointId) ?? '—'}</td>
                  <td data-label={k('change')} className={classes.parameter}>
                    {u.changes
                      .filter((field) => (CHANGE_FIELDS as readonly string[]).includes(field))
                      .map(
                        (field) =>
                          `${k(`fields.${field}`)}: ${show(field, u.before)} → ${show(field, u.after)}`,
                      )
                      .join('; ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      ) : null}
      {report.defectsToOpen.length > 0 ? (
        <Section title={k('defectsTitle')}>
          <table className={pms.table} data-compact data-cards>
            <thead>
              <tr>
                <th scope="col">{k('row')}</th>
                <th scope="col">{k('point')}</th>
                <th scope="col">{k('value')}</th>
                <th scope="col">{k('measuredAt')}</th>
              </tr>
            </thead>
            <tbody>
              {report.defectsToOpen.map((d) => (
                <tr key={`${d.row}`}>
                  <td data-label={k('row')}>{d.row}</td>
                  <td data-label={k('point')}>{nameOf(d.point)}</td>
                  <td data-label={k('value')}>
                    {d.value === null ? '—' : `${measureNumber(language, d.value)} ${d.unit ?? ''}`}
                  </td>
                  <td data-label={k('measuredAt')}>{formatDateTime(d.measuredAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      ) : null}
    </ImportPreview>
  );
}
